/**
 * Any LTI integrations API calls with React Query.
 */
import {
    GetLtiIntegrationDetail,
    GetLtiIntegrations,
} from "../../services/LtiIntegrationService";
import { appQueryClient } from "../appQueryClient";
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from "../queryConsts";
import { useBaseReactQuery } from "./useBaseReactQuery";

const EMPTY_INTEGRATIONS_LIST = Object.freeze({
    integrations: [],
    totalRows: 0
});

const EMPTY_INTEGRATION_DETAIL = Object.freeze({});

/**
 * Marks LTI integrations list and detail caches stale.
 * Only currently mounted (active) queries refetch immediately; inactive ones
 * (e.g. the table list while on the details page) refetch on next mount.
 * @returns {Promise} React Query invalidation promise
 */
export function invalidateIntegrationsQueries() {
    return appQueryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.integrations.root],
    });
}

/**
 * LTI Integrations List
 * @param {object} requestData Request body passed directly to the API.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useIntegrationsList(requestData) {
    const query = useBaseReactQuery(
        [
            QUERY_KEYS.integrations.root,
            QUERY_KEYS.integrations.integrationsList,
            requestData
        ],
        async () => {
            const { data } = await GetLtiIntegrations(requestData);

            return {
                integrations: data?.integrations?.data ?? [],
                totalRows: data?.integrations?.total ?? 0
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_INTEGRATIONS_LIST
    };
}

/**
 * LTI Integration Detail
 * @param {string|number} id Integration id.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useIntegrationDetail(id) {
    const query = useBaseReactQuery(
        [
            QUERY_KEYS.integrations.root,
            QUERY_KEYS.integrations.integrationDetail,
            id
        ],
        async () => {
            const { data } = await GetLtiIntegrationDetail(id);

            return data?.integration ?? EMPTY_INTEGRATION_DETAIL;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DETAILS,
            enabled: Boolean(id)
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_INTEGRATION_DETAIL
    };
}
